import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Grid } from '@opendoor/atoms/grid';
import { cn } from '@opendoor/atoms/utils';

import { trapFocus } from '../trapFocus';
import type { MenuConfig, NavigationProps } from '../types';
import { MenuContent } from './components/MenuContent';

interface MenuPanelProps extends NavigationProps, MenuConfig {
  isVisible?: boolean;
  setIsVisible: (isVisible: boolean) => void;
  exitComplete: () => void;
  variant?: 'default' | 'glass';
}

const Menu = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  megaNavData,
  variant = 'default',
}: MenuPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Fade the backdrop in on the frame after mount so the CSS transition runs.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);
  const open = Boolean(isVisible) && entered;

  const onComplete = useCallback(
    (definition: 'visible' | 'hidden') => {
      if (definition === 'visible') {
        containerRef.current?.focus();
      } else {
        exitComplete();
      }
    },
    [exitComplete],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => trapFocus(event, containerRef),
    [],
  );
  const handleBackdropClick = useCallback(() => setIsVisible(false), [setIsVisible]);

  // The glass header remains visible above the menu, including any visible promotion.
  const insetClass =
    variant === 'glass'
      ? 'inset-x-0 bottom-0 top-[calc(var(--spacing)*18+var(--site-promo-banner-visible-height,0px))]'
      : 'inset-0';

  return (
    <Box className={cn('fixed z-[1000]', insetClass)} onKeyDown={handleKeyDown}>
      <Grid className="h-full w-full">
        <MenuContent
          isVisible={!!isVisible}
          containerRef={containerRef}
          onAnimationComplete={onComplete}
          slug={slug}
          menuPanelNavLinks={menuPanelNavLinks}
          mainNavLinks={mainNavLinks}
          ctaLink={ctaLink}
          ctaTitle={ctaTitle}
          megaNavData={megaNavData}
          variant={variant}
        />
        {/* eslint-disable-next-line @opendoor/no-html-elements -- click-to-close overlay stays a non-focusable div (a button would escape the dialog focus trap) */}
        <div
          aria-hidden="true"
          onClick={handleBackdropClick}
          className={cn(
            'fixed z-0 cursor-pointer bg-black/20 transition-opacity duration-200 ease-out',
            insetClass,
            open ? 'opacity-100' : 'opacity-0',
          )}
        />
      </Grid>
    </Box>
  );
};

const MenuPanel = ({
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  isVisible,
  setIsVisible,
  exitComplete,
  megaNavData,
  variant = 'default',
}: MenuPanelProps) => {
  const [mounted, setMounted] = useState(false);
  const [shouldRender, setShouldRender] = useState(Boolean(isVisible));
  const isVisibleRef = useRef(Boolean(isVisible));
  // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
  isVisibleRef.current = Boolean(isVisible);
  // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (isVisible) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setShouldRender(true);
    }
  }, [isVisible]);

  useEffect(() => {
    document.body.classList.toggle('has-sheet', isVisible);
    return () => document.body.classList.remove('has-sheet');
  }, [isVisible]);

  const escFunction = useCallback(
    (event: { key: string }) => {
      if (event.key === 'Escape') {
        setIsVisible(false);
      }
    },
    [setIsVisible],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const handleExitComplete = useCallback(() => {
    if (!isVisibleRef.current) {
      setShouldRender(false);
    }
    exitComplete();
  }, [exitComplete]);

  return mounted && shouldRender ? (
    <Menu
      slug={slug}
      menuPanelNavLinks={menuPanelNavLinks}
      mainNavLinks={mainNavLinks}
      ctaLink={ctaLink}
      ctaTitle={ctaTitle}
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      exitComplete={handleExitComplete}
      megaNavData={megaNavData}
      variant={variant}
    />
  ) : null;
};

export default MenuPanel;
